import React from 'react';

import ResourceAutocomplete from './resource-autocomplete';
import * as enums from '../enums';
import { _, mui, useSelector } from '../libs';
import * as types from '../types';
import { BaseStore } from '../types';

type IndexSelectorProps = {
  value: string[] | string;
  setValue: (_v: string[] | string) => void;
  disabled?: boolean;
  errorState?: boolean;
  multiple?: boolean;
};

/**
 * IndexSelector component that displays a dropdown for selecting an index.
 *
 * @requires Redux Store:
 * - state.resources.data_sources: The data sources available for selection in the dropdown.
 *
 * @param value - The value of the selected index.
 * @param setValue - The function to set the value of the selected index.
 * @param disabled - Optional flag to disable the dropdown.
 * @param errorState - Optional flag to indicate if the component is in an error state.
 * @param multiple - Optional flag to indicate if the component should allow multiple selections.
 *
 * @returns A React component that displays a dropdown for selecting an index.
 */

const IndexSelector = ({ value, setValue, disabled, errorState, multiple }: IndexSelectorProps) => {
  const datasources = useSelector((store: BaseStore) => store.resources.data_sources);

  const providers = React.useMemo(() => {
    const providers = datasources
      .filter((ds) => ds.is_index)
      .map((u) => ({
        key: u.data_provider_label || 'unknown',
        label: u.data_provider_label || 'Unknown',
      }));
    return _.uniqBy(providers, 'key');
  }, [datasources]);

  return (
    <ResourceAutocomplete<types.dataSource.DataSourceExpanded, typeof multiple, true>
      chooseHandle
      value={value}
      setValue={(v) => setValue(v)}
      type={enums.RESOURCES_TYPES_ENUM.DATA_SOURCE}
      disabled={disabled}
      filter={(d) => d.is_index}
      customInputFilters={
        [
          {
            key: 'provider',
            label: 'Data Provider',
            type: 'select' as 'select' | 'text',
            options: providers,
            icon: () => <mui.icons.Apartment />,
          },
        ] as types.guidedSearch.Filter[]
      }
      inputProps={{
        fullWidth: true,
        label: 'Index',
        variant: 'outlined',
        size: 'small',
        required: true,
        disabled: disabled,
        error: errorState,
      }}
      autocompleteProps={{
        multiple,
        size: 'small',
      }}
    />
  );
};

export default IndexSelector;
